import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'outline';

export interface GenericStatusPageAction {
  label: string;
  to?: string;
  onClick?: () => void;
  variant?: ButtonVariant;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface GenericStatusPageProps {
  statusCode?: string;
  title?: string;
  message?: string;
  description?: string;
  icon?: ReactNode;
  iconColor?: string;
  actions?: GenericStatusPageAction[];
  showBackButton?: boolean;
  containerClassName?: string;
  children?: ReactNode;
}

const GenericStatusPage: React.FC<GenericStatusPageProps> = ({
  statusCode,
  title,
  message,
  description,
  icon,
  iconColor = 'text-red-500',
  actions = [],
  showBackButton = true,
  containerClassName = '',
  children
}) => {
  const defaultActions: GenericStatusPageAction[] = showBackButton ? [
    {
      label: 'Retour au tableau de bord',
      to: '/bord',
      variant: 'secondary'
    }
  ] : [];

  const allActions = [...defaultActions, ...actions];

  const getButtonClasses = (variant: ButtonVariant = 'primary'): string => {
    const baseClasses = 'inline-flex items-center px-4 py-2 rounded-md font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

    switch (variant) {
      case 'primary':
        return `${baseClasses} bg-primary text-white hover:bg-primary/90`;
      case 'secondary':
        return `${baseClasses} bg-secondary text-white hover:bg-secondary/90`;
      case 'success':
        return `${baseClasses} bg-green-600 text-white hover:bg-green-700 focus:ring-green-500`;
      case 'warning':
        return `${baseClasses} bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500`;
      case 'danger':
        return `${baseClasses} bg-red-600 text-white hover:bg-red-700 focus:ring-red-500`;
      case 'outline':
        return `${baseClasses} border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-gray-500`;
      default:
        return `${baseClasses} bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500`;
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 ${containerClassName}`}>
      <div className="max-w-md w-full space-y-8 text-center">
        {/* Icône */}
        {icon && (
          <div className="flex justify-center">
            <div className={`text-6xl ${iconColor}`}>
              {icon}
            </div>
          </div>
        )}

        {/* Code de statut */}
        {statusCode && (
          <div className="text-6xl font-bold text-gray-900">
            {statusCode}
          </div>
        )}

        {/* Titre */}
        {title && (
          <h1 className="text-3xl font-bold text-gray-900">
            {title}
          </h1>
        )}

        {/* Message principal */}
        {message && (
          <h2 className="text-xl font-semibold text-gray-700">
            {message}
          </h2>
        )}

        {/* Description */}
        {description && (
          <p className="text-gray-600 max-w-sm mx-auto">
            {description}
          </p>
        )}

        {/* Contenu personnalisé */}
        {children && (
          <div className="mt-6">
            {children}
          </div>
        )}

        {/* Actions */}
        {allActions.length > 0 && (
          <div className="mt-8 space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            {allActions.map((action, index) => (
              <div key={index}>
                {action.to ? (
                  <Link
                    to={action.to}
                    className={getButtonClasses(action.variant)}
                    onClick={action.onClick}
                  >
                    {action.icon && <span className="mr-2">{action.icon}</span>}
                    {action.label}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={action.onClick}
                    className={getButtonClasses(action.variant)}
                    disabled={action.disabled}
                  >
                    {action.icon && <span className="mr-2">{action.icon}</span>}
                    {action.label}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericStatusPage;
